import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { budgetDataService } from 'services/budget.service';

import { Actions } from 'components/Actions';
import { DeleteModal } from 'components/DeleteModal';
import { Icon } from 'components/Icons';

import * as S from './BudgetActions.styles';

export type BudgetRow = {
  id: string;
  season?: string;
  marketingLine?: string;
  genderCode?: string;
  zone?: string;
  type?: string;
  num?: number;
  budgetStatus?: string;
};

type BudgetActionsProps = {
  row: BudgetRow;
  onChanged: () => void;
};

type MenuPosition = { right: number } & ({ top: number } | { bottom: number });

// Room the menu needs below its trigger before it opens upwards instead
const MENU_HEIGHT = 96;

const sanitizeCode = (v?: string) =>
  typeof v === 'string' ? v.replace(/\s+/g, '').trim() : '';

const sanitizeLine = (v?: string) =>
  typeof v === 'string' ? v.trim().replace(/\s+/g, ' ') : '';

/**
 * Row actions of the admin Budget list: Manage, plus a "more" menu with
 * Reopen (budgets sent from the catalogue only) and Delete, each confirmed
 * in a modal. The menu is position: fixed so the table wrapper's
 * overflow: auto does not clip it on the last rows.
 */
export function BudgetActions({ row, onChanged }: BudgetActionsProps): React.ReactElement {
  const [menuPosition, menuPositionSet] = useState<MenuPosition | null>(null);
  const [confirm, confirmSet] = useState<'reopen' | 'delete' | null>(null);
  const [isLoading, isLoadingSet] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const isSent = row.budgetStatus?.toLowerCase() === 'sent';
  const isOpen = menuPosition !== null;

  const closeMenu = () => menuPositionSet(null);

  const toggleMenu = () => {
    if (isOpen || !triggerRef.current) {
      closeMenu();
      return;
    }

    const rect = triggerRef.current.getBoundingClientRect();
    const right = window.innerWidth - rect.right;

    menuPositionSet(
      rect.bottom + MENU_HEIGHT > window.innerHeight
        ? { right, bottom: window.innerHeight - rect.top + 4 }
        : { right, top: rect.bottom + 4 }
    );
  };

  // A fixed menu would drift away from its row, so any scroll or resize closes it
  useEffect(() => {
    if (!isOpen) return undefined;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) closeMenu();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('resize', closeMenu);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [isOpen]);

  const choose = (action: 'reopen' | 'delete') => {
    closeMenu();
    confirmSet(action);
  };

  const reopen = async () => {
    if (!row.zone || row.num === undefined) return;

    confirmSet(null);
    isLoadingSet(true);
    try {
      await budgetDataService.reopen(row.zone, row.num);
      toast.success(`Budget ${row.num} reopened`);
      onChanged();
    } catch {
      // the http-common interceptor already shows the error toast
    } finally {
      isLoadingSet(false);
    }
  };

  const remove = async () => {
    confirmSet(null);
    isLoadingSet(true);
    try {
      await budgetDataService.delete(row.id, {
        season: sanitizeCode(row.season),
        seasonLine: sanitizeLine(row.marketingLine),
        genderCode: sanitizeCode(row.genderCode),
        zone: sanitizeCode(row.zone),
        type: sanitizeCode(row.type),
      });
      toast.success('Budget deleted successfully');
      onChanged();
    } catch {
      toast.error('Failed to delete budget');
    } finally {
      isLoadingSet(false);
    }
  };

  return (
    <S.Container ref={containerRef}>
      <Actions isManagePath toTheRight id={row.id} rowData={row} />

      <S.Trigger
        ref={triggerRef}
        type="button"
        aria-label={`More actions for budget ${row.num ?? ''}`.trim()}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        disabled={isLoading}
        onClick={toggleMenu}
      >
        <Icon name="FiMoreVertical" aria-hidden />
      </S.Trigger>

      {menuPosition ? (
        <S.Menu role="menu" style={menuPosition}>
          {isSent ? (
            <S.MenuItem type="button" role="menuitem" onClick={() => choose('reopen')}>
              <Icon name="FiUnlock" aria-hidden />
              Reopen
            </S.MenuItem>
          ) : undefined}

          <S.MenuItem type="button" role="menuitem" $isDanger onClick={() => choose('delete')}>
            <Icon name="FiTrash2" aria-hidden />
            Delete
          </S.MenuItem>
        </S.Menu>
      ) : undefined}

      {confirm === 'reopen' ? (
        <DeleteModal
          customText={`Reopen budget ${row.num} (zone ${row.zone})? Agents will be able to change its quantities again.`}
          onDelete={reopen}
          onClose={() => confirmSet(null)}
        />
      ) : undefined}

      {confirm === 'delete' ? (
        <DeleteModal name="this budget" onDelete={remove} onClose={() => confirmSet(null)} />
      ) : undefined}
    </S.Container>
  );
}
